import { CommonModule } from '@angular/common';
import { Component, input, OnChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MapFilterCategory, MapFilterResult, TabMapFilter } from 'weavix-shared/models/weavix-map.model';
import { AutoUnsubscribe, Utils } from 'weavix-shared/utils/utils';

@AutoUnsubscribe()
@Component({
    selector: 'app-weavix-map-filter-tab',
    templateUrl: './weavix-map-filter-tab.component.html',
    styleUrls: ['./weavix-map-filter-tab.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
    ],
})
export class WeavixMapFilterTabComponent implements OnChanges {
    readonly tabMapFilters = input<TabMapFilter[]>([]);
    readonly mapFilterResults = input<{ [key: string]: MapFilterResult }>({});
    /** Categories whose tabs are mutually exclusive; empty keeps the per-category behavior. */
    readonly exclusiveCategories = input<MapFilterCategory[]>([]);

    ngOnChanges(): void {
        this.ensureOneTabSelected();
    }

    private ensureOneTabSelected(): void {
        const tabs = this.tabMapFilters();
        const results = this.mapFilterResults();

        if (!tabs?.length || !results) return;

        const isVisible = (tab: TabMapFilter) => !tab.hidden && !!results[tab.category]?.children?.[tab.childKey]?.childCount;
        const firstVisibleTab = tabs.find(isVisible);

        if (!firstVisibleTab) return;

        const anyTabSelected = tabs.some(tab => isVisible(tab) && results[tab.category]?.children?.[tab.childKey]?.selected);

        if (!anyTabSelected) {
            const parent = results[firstVisibleTab.category];
            const mapFilterResult = parent?.children?.[firstVisibleTab.childKey];

            if (parent && mapFilterResult) {
                this.deselectExclusiveCategories(mapFilterResult);
                this.deselectCategory(parent, mapFilterResult);
                mapFilterResult.selected = true;
                mapFilterResult.setSelected?.(true, true, true);
            }
        }
    }

    getSortedChildren(result: MapFilterResult) {
        return Utils.sortAlphabetical(Object.values(result.children ?? {}), res => res.name?.toLocaleLowerCase());
    }

    deselectAllCategories(): void {
        Object.values(this.mapFilterResults()).forEach(cat => {
            this.deselectCategory(cat);
            cat.selected = false;
            cat.setSelected?.(cat.selected, true);
        });
    }

    deselectCategory(cat: MapFilterResult, ignore?): void {
        if (cat.children) {
            cat.childrenSelected = Object.keys(cat?.children ?? {}).some(k => cat?.children[k].selected);
            Object.values(cat.children).forEach(subCat => {
                if (subCat === ignore || !subCat.selected) return;
                subCat.selected = false;
                subCat.setSelected?.(subCat.selected, true);
                this.deselectCategory(subCat, ignore);
            });
        }
    }

    handleClick(tab: TabMapFilter): void {
        if (tab.hidden) return;
        if (!tab.childKey) return;
        if (!tab.category) return;

        const parent = this.mapFilterResults()?.[tab.category];
        const mapFilterResult = parent?.children?.[tab.childKey];

        if (!parent || !mapFilterResult) return;

        this.deselectExclusiveCategories(mapFilterResult);
        this.deselectCategory(parent, mapFilterResult);
        if (!mapFilterResult.selected) {
            mapFilterResult.selected = true;
            mapFilterResult.setSelected?.(true, true, true);
        }
    }

    /** Deselects every selected child in the exclusive categories except the given tab result. */
    private deselectExclusiveCategories(keep: MapFilterResult): void {
        this.exclusiveCategories().forEach(category => {
            const parent = this.mapFilterResults()?.[category];
            if (parent) this.deselectCategory(parent, keep);
        });
    }

    isTabSelected(tab: TabMapFilter): boolean {
        const mapFilterResult = this.mapFilterResults()[tab.category]?.children?.[tab.childKey];
        if (!mapFilterResult) return false;
        return mapFilterResult.selected;
    }
}
